import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../style/AppPages.css";
import "../style/Order.css";
import { fetchStripeOrder } from "../controllers/AccountConnectionLayer";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../contexts/CurrencyContext";

const OrderPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const order = location.state?.order;
    const [orderStripe, setOrderStripe] = useState(null);
    const [billingDetails, setBillingDetails] = useState(null);
    const [couponAmount, setCouponAmount] = useState(null);
    const [giftcardAmount, setGiftcardAmount] = useState(null);

    // const [methodOfPayment, setMethodOfPayment] = useState("Unknown");
    const [error, setError] = useState(null);
    const { getCurrencySymbolByCode, getCurrencyDecimalsByCode, formatPrice } = useCurrency();

    // Parse metadata into items
    const parsedItems = order?.metadata
        ? order.metadata.map((item) => JSON.parse(item))
        : [];
    const extractBillingDetails = (metadata) => {
        if (!metadata) return null;
    
        const billingDetails = {
            name: metadata.billing_name || null,
            line1: metadata.billing_line1 || null,
            line2: metadata.billing_line2 || null,
            city: metadata.billing_city || null,
            state: metadata.billing_state || null,
            postalCode: metadata.billing_postal_code || null,
            country: metadata.billing_country || null,
            phone: metadata.billing_phone || null,
        };
    
        return Object.values(billingDetails).every(value => value === null) ? null : billingDetails;
    };  

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const stripeData = await fetchStripeOrder(order.stripePaiementId);

                setOrderStripe(stripeData);

                setBillingDetails(extractBillingDetails(stripeData.metadata));

                if (stripeData.metadata.couponReduction !== "0"){
                    setCouponAmount(stripeData.metadata.couponReduction);
                }
                if (stripeData.metadata.cardAmount !== "0")
                    setGiftcardAmount(stripeData.metadata.cardAmount);
            } catch (err) {
                setError(t("order.fetchFailed"));
            }
        };

        fetchOrder();
    }, []);

    if (!order) {
        return <div className="page-padding"><h1>{t("order.notFound")}</h1></div>;
    }

    return (
        <div className="page-padding">
            <div className="order-info-container make-smaller align-left">
                <div className="inner-padding">
                    <div className="order-info-header-upper">
                        <h1 className="order-title">{t("order.order")} #{order.order_number}</h1>
                        <button className="form-button top-right" onClick={() => navigate('/account/update', { state: { tab: "orders" } })}>{t("order.back")}</button>
                    </div>
                    <div className="order-info-header">
                        <h3>{t("orders.orderStatus")}: {order.status}</h3>
                        {order.tracking &&
                        <h3>{t("orders.tracking")}: {order.tracking}</h3>
                        }
                        <h3>
                            {t("orders.orderDate")}: {" "}
                            {order.created_at
                                ? new Date(order.created_at).toLocaleString("en-CA", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                    hour12: false,
                                }).replace(/,/, ` ${t("orders.orderJoin")} `)
                                : t("order.unknownDate")}
                        </h3>
                    </div>
                    <div className="order-info-data">
                        <div className="address-container preferences-section">
                            <div className="shipped-with-container">
                                <h2>{t("order.shippedWith")}: {orderStripe?.metadata?.shippingName || t("order.shippedUnknown")}</h2>
                                <div class="left-horizontal-container">
                                    <div class="small-horizontal"></div>
                                </div>
                            </div>
                            <div className="address-info">
                                <div className="address-data">
                                    <h2>{t("order.shippingInfoLabel")}</h2>
                                    <p>{t("order.shippedWith")}: {orderStripe?.metadata?.shippingName || "Unknown"}</p>
                                    <p>{orderStripe?.shipping?.name}</p>
                                    <p>
                                    {orderStripe?.shipping?.address?.line2
                                        ? `${orderStripe.shipping.address.line2} - ${orderStripe.shipping.address.line1}`
                                        : orderStripe?.shipping?.address?.line1}
                                    </p>
                                    <p>{orderStripe?.shipping?.address?.city}, {orderStripe?.shipping?.address?.state}, {orderStripe?.shipping?.address?.postal_code}</p>
                                    <p>{orderStripe?.shipping?.address?.country}</p>
                                </div>

                                {billingDetails && Object.values(billingDetails).some(value => value !== null && value !== 'undefined' && value !== '' && value !== undefined) && (
                                    <div className="address-data">
                                        <h2>{t("order.billingInfoLabel")}</h2>
                                        <p>
                                        {billingDetails.line2 && billingDetails.line2 !== "null"
                                            ? `${billingDetails.line2} - ${billingDetails.line1}`
                                            : billingDetails.line1}
                                        </p>
                                        <p>{billingDetails.city}, {billingDetails.state}, {billingDetails.postalCode}</p>
                                        <p>{billingDetails.country}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="order-items-price-container preferences-section">
                            {/* Ordered Items Section */}
                            <div>
                                <div className="ordered-items-container">
                                    <h2>{t("order.itemsLabel")}</h2>
                                    <div class="left-horizontal-container">
                                        <div class="small-horizontal"></div>
                                    </div>
                                </div>
                                    
                                <ul>
                                    {parsedItems.map((item, index) => (
                                        <li key={index} className="order-item">
                                            <img className="cart-image-preview cart-image-phone" src={item.previewUrl} alt={item.name} />
                                            <div className="order-item-details">
                                                <p className="order-item-name"><strong>{item.name}</strong></p>
                                                <div className="order-details">
                                                    <p>{t("checkout.priceLabel")}: {item.symbol || "$"}{formatPrice(item.totalPrice, item.currency || "CAD")} {item.currency || "CAD"}</p>
                                                    <p>{t("checkout.sizeLabel")}: {t("options.size" + item.selectedSize)}  | </p>
                                                    {item.selectedFirmness && item.selectedFirmness !== "N/A" && (
                                                                <p>
                                                                    {t("checkout.softnessLabel")}: {typeof item.selectedFirmness?.description === "string"
                                                                    ? t("options.firmness" + item.selectedFirmness.description).replace(/Firmness|Souplesse/gi, "").trim()
                                                                    : t("options.firmness" + item.selectedFirmness) || "N/A"} | 
                                                                </p>
                                                    )}
                                                    {item.selectedColor && item.selectedColor !== "N/A" && (
                                                        <p>{t("checkout.colorLabel")}: {item.selectedColor || "N/A"} | </p>
                                                    )}
                                                    <p>{t("checkout.optionsLabel")}: {item.selectedOption ? t("options.option" + item.selectedOption) : "N/A"}</p>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div class="small-horizontal-container">
                                <div class="small-horizontal"></div>
                            </div>

                            {/* Payment Information Section */}
                            <div>
                                <h2>{t("order.paymentInfoLabel")}</h2>
                                {/* <p>Paid via: {methodOfPayment}</p> */}
                                <p>{t("checkout.subtotalLabel")}: {getCurrencySymbolByCode(orderStripe?.currency)}{formatPrice(orderStripe?.metadata?.preTotal, orderStripe?.currency)} {String(orderStripe?.currency || "").toUpperCase()}</p>
                                {/* <p>Taxes: ${parseFloat(orderStripe?.metadata?.taxes).toFixed(2) || "0.00"}</p> */}
                                {couponAmount && (
                                    <p>
                                        {t("checkout.discountLabel")}: -{getCurrencySymbolByCode(orderStripe?.currency)}{formatPrice(couponAmount, orderStripe?.currency)} {String(orderStripe?.currency || "").toUpperCase()}
                                    </p>
                                )}
                                {giftcardAmount && (
                                    <p>
                                        {t("checkout.giftcardLabel")}: -{getCurrencySymbolByCode(orderStripe?.currency)}{formatPrice(giftcardAmount, orderStripe?.currency)} {String(orderStripe?.currency || "").toUpperCase()}
                                    </p>
                                )}
                                <p>{t("checkout.shippingLabel")}: 
                                    {orderStripe?.metadata?.freeShipping === "true" ? (
                                        <>
                                            <span className="free-shipping-price">
                                               {" "}{getCurrencySymbolByCode(orderStripe?.currency)}{formatPrice(orderStripe?.metadata?.shippingPrice, orderStripe?.currency)} {String(orderStripe?.currency || "").toUpperCase()}</span>
                                               {" "}<span className="free-shipping-label">{t("checkout.freeLabel")}</span>

                                        </>
                                    ) : (
                                        <>
                                            {" "}{getCurrencySymbolByCode(orderStripe?.currency)}{formatPrice(orderStripe?.metadata?.shippingPrice, orderStripe?.currency)} {String(orderStripe?.currency || "").toUpperCase()}
                                        </>
                                    )}
                                </p>
                                <p><strong>{t("checkout.totalLabel")}: {getCurrencySymbolByCode(orderStripe?.currency)}{formatPrice(
                                    getCurrencyDecimalsByCode(orderStripe?.currency)
                                        ? orderStripe?.amount / 100
                                        : orderStripe?.amount,
                                    orderStripe?.currency
                                ) || "0.00"} {String(orderStripe?.currency || "").toUpperCase()}</strong></p>
                            </div>
                        </div>
                    </div>

                    {error && <p className="error-message">{error}</p>}
                </div>
            </div>
        </div>
    );
};

export default OrderPage;
